/**
 * Browser-side `reset_display`: view-only, auto-run like a read. Each stage
 * is one of the viewer's existing reset buttons:
 *  - view: the toolbar's Reset View (zoom to fit, crosshair to the image
 *    centre — so the shown slice changes);
 *  - window-level: the overlay's Reset, for each loaded image;
 *  - contour-thickness, dose-opacity, layout: their slider resets.
 * `all` runs every stage in that order. A stage that does not apply (no
 * image, no dose) is skipped with its reason; the others still run.
 * Contours and structures are never touched. Ports injected (React-free).
 */
import { asRecord } from '../core';
import type { ResetDisplayOutput } from '../../contract';

export const RESET_STAGES = [
    'view',
    'window-level',
    'contour-thickness',
    'dose-opacity',
    'layout',
] as const;
export type ResetStage = (typeof RESET_STAGES)[number];

/** Each stage's reset; returns why it was skipped, or undefined if done. */
export type ResetDisplayDeps = Record<ResetStage, () => string | undefined>;

export function resetDisplay(
    input: unknown,
    deps: ResetDisplayDeps
): ResetDisplayOutput {
    const scope = asRecord(input)?.scope;
    const stages: ReadonlyArray<ResetStage> =
        scope === 'all'
            ? RESET_STAGES
            : RESET_STAGES.filter((s) => s === scope);
    if (stages.length === 0) {
        return {
            status: 'ok',
            reset: [],
            skipped: [
                {
                    stage: 'all',
                    reason:
                        'unknown scope; use view, window-level, ' +
                        'contour-thickness, dose-opacity, layout or all',
                },
            ],
        };
    }
    const reset: ResetStage[] = [];
    const skipped: { stage: ResetStage; reason: string }[] = [];
    for (const stage of stages) {
        const why = deps[stage]();
        if (why === undefined) {
            reset.push(stage);
        } else {
            skipped.push({ stage, reason: why });
        }
    }
    return { status: 'ok', reset, skipped };
}
